import React, { useState, useEffect, useCallback, useRef } from 'react';
import { photoFixAPI } from '../api/client';

// ── Категорії ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  {
    key:     'products',
    label:   'Каталог товарів',
    columns: [
      { key: 'sku',       label: 'SKU',     width: 120 },
      { key: 'name',      label: 'Назва',   flex: 1 },
      { key: 'photo_url', label: 'URL фото', flex: 1 },
    ],
  },
  {
    key:     'clothing_products',
    label:   'Каталог одягу',
    columns: [
      { key: 'sku',       label: 'SKU',     width: 120 },
      { key: 'name',      label: 'Назва',   flex: 1 },
      { key: 'photo_url', label: 'URL фото', flex: 1 },
    ],
  },
  {
    key:     'supplier_items',
    label:   'Фото постачальників (взуття)',
    columns: [
      { key: 'sku',           label: 'SKU',          width: 120 },
      { key: 'supplier_name', label: 'Постачальник',  width: 180 },
      { key: 'photo_url',     label: 'URL фото',      flex: 1 },
    ],
  },
  {
    key:     'clothing_supplier_items',
    label:   'Фото постачальників (одяг/сумки)',
    columns: [
      { key: 'sku',           label: 'SKU',          width: 120 },
      { key: 'supplier_name', label: 'Постачальник',  width: 180 },
      { key: 'photo_url',     label: 'URL фото',      flex: 1 },
    ],
  },
  {
    key:     'supplier_items_broken',
    label:   '🔗 Зламані фото (TG-посилання)',
    columns: [
      { key: 'sku',           label: 'SKU',          width: 120 },
      { key: 'supplier_name', label: 'Постачальник',  width: 180 },
      { key: 'photo_url',     label: 'Telegram-посилання', flex: 1 },
    ],
  },
];

const PAGE_SIZE = 50;

// ── Головний компонент ───────────────────────────────────────────────────────
export default function PhotoFixTab() {
  const [stats,    setStats]    = useState(null);
  const [category, setCategory] = useState('products');
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [offset,   setOffset]   = useState(0);
  const [sortBy,   setSortBy]   = useState('sku');
  const [sortDir,  setSortDir]  = useState('asc');
  const [loading,   setLoading]   = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected,  setSelected]  = useState(null);
  const [search,       setSearch]       = useState('');
  const [tgFailedOnly, setTgFailedOnly] = useState(false);
  const searchTimerRef = useRef(null);

  // Google Sheets sync
  const [syncing,          setSyncing]          = useState(null); // 'catalog' | 'supplier' | null
  const [syncResultCatalog,  setSyncResultCatalog]  = useState(null);
  const [syncResultSupplier, setSyncResultSupplier] = useState(null);

  // Bulk download imgbox
  const [bulkRunning,  setBulkRunning]  = useState(false);
  const [bulkResult,   setBulkResult]   = useState(null);
  const bulkPollRef = useRef(null);

  // Telegram download
  const [tgRunning, setTgRunning] = useState(false);
  const [tgResult,  setTgResult]  = useState(null);
  const tgPollRef = useRef(null);

  const [stubCleanResult, setStubCleanResult] = useState(null);
  const [stubCleanRunning, setStubCleanRunning] = useState(false);

  // Price sheets sync (E→F)
  const [priceRunning, setPriceRunning] = useState(false);
  const [priceResult,  setPriceResult]  = useState(null);
  const pricePollRef = useRef(null);

  // Розсилка всіх фото адміну в Telegram
  const [sendRunning,  setSendRunning]  = useState(false);
  const [sendResult,   setSendResult]   = useState(null);
  const [sendLimit,    setSendLimit]    = useState(50);
  const [sendFailedOpen, setSendFailedOpen] = useState(false);
  const [sendFailedList, setSendFailedList] = useState([]);
  const sendPollRef = useRef(null);

  // Clothing catalog sync (стовпець C)
  const [clothingRunning, setClothingRunning] = useState(false);
  const [clothingResult,  setClothingResult]  = useState(null);
  const clothingPollRef = useRef(null);

  // EASYBDPROM
  const [easyRunning,   setEasyRunning]   = useState(false);
  const [easyResult,    setEasyResult]    = useState(null);
  const [easyLimit,     setEasyLimit]     = useState(20);
  const [easyStartFrom, setEasyStartFrom] = useState('');
  const easyPollRef = useRef(null);

  // Queue
  const [queueRunning, setQueueRunning] = useState(false);
  const [queueResult,  setQueueResult]  = useState(null);
  const queuePollRef = useRef(null);

  // Image Cache viewer
  const [icOpen,    setIcOpen]    = useState(false);
  const [icSearch,  setIcSearch]  = useState('');
  const [icStatus,  setIcStatus]  = useState('all');
  const [icPage,    setIcPage]    = useState(1);
  const [icItems,   setIcItems]   = useState([]);
  const [icTotal,   setIcTotal]   = useState(0);
  const [icLoading, setIcLoading] = useState(false);
  const icSearchTimer = useRef(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await photoFixAPI.getStats();
      setStats(r.data);
    } catch {}
  }, []);

  const loadItems = useCallback(async (cat, off, sb, sd, q, failedOnly) => {
    setLoading(true);
    setLoadError(false);
    try {
      const r = await photoFixAPI.getMissing(cat, {
        offset:         off,
        limit:          PAGE_SIZE,
        sort_by:        sb,
        sort_dir:       sd,
        search:         q ?? '',
        tg_failed_only: failedOnly ?? false,
      });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch { setLoadError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setOffset(0);
    setSearch('');
    setTgFailedOnly(false);
    loadItems(category, 0, sortBy, sortDir, '', false);
  }, [category, sortBy, sortDir]); // eslint-disable-line

  function handleSearch(val) {
    setSearch(val);
    setOffset(0);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      loadItems(category, 0, sortBy, sortDir, val, tgFailedOnly);
    }, 350);
  }

  function toggleFailedOnly() {
    const next = !tgFailedOnly;
    setTgFailedOnly(next);
    setOffset(0);
    loadItems(category, 0, sortBy, sortDir, search, next);
  }

  function handleSort(col) {
    if (col === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  function handlePage(dir) {
    const next = Math.max(0, offset + dir * PAGE_SIZE);
    setOffset(next);
    loadItems(category, next, sortBy, sortDir, search, tgFailedOnly);
  }

  const catDef = CATEGORIES.find(c => c.key === category);

  async function handleSyncCatalog() {
    const controller = new AbortController();
    catalogAbortRef.current = controller;
    setSyncing('catalog');
    setSyncResultCatalog(null);
    try {
      const r = await photoFixAPI.syncCatalogSheet({ signal: controller.signal });
      setSyncResultCatalog(r.data);
      loadStats();
    } catch (e) {
      if (e?.code === 'ERR_CANCELED') {
        setSyncResultCatalog({ status: 'stopped', message: 'Скасовано' });
      } else {
        setSyncResultCatalog({ error: e?.response?.data?.detail || 'Помилка синхронізації' });
      }
    } finally {
      catalogAbortRef.current = null;
      setSyncing(null);
    }
  }

  function handleStopCatalog() {
    catalogAbortRef.current?.abort();
  }

  const supplierPollRef  = useRef(null);
  const catalogAbortRef  = useRef(null);

  async function handleSyncSupplier() {
    setSyncing('supplier');
    setSyncResultSupplier(null);
    try {
      await photoFixAPI.syncSupplierSheets();
    } catch (e) {
      setSyncResultSupplier({ error: e?.response?.data?.detail || 'Помилка запуску' });
      setSyncing(null);
      return;
    }
    // Polling статусу кожні 2 секунди
    supplierPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.syncSupplierSheetsStatus();
        const data = s.data;
        setSyncResultSupplier(data);
        if (data.status === 'done' || data.status === 'error') {
          clearInterval(supplierPollRef.current);
          setSyncing(null);
          loadStats();
        }
      } catch {}
    }, 2000);
  }

  // При монтуванні перевіряємо чи є активна задача і автовідновлюємо polling
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.syncSupplierSheetsStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setSyncResultSupplier(data);
        if (data.status === 'running') {
          setSyncing('supplier');
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.syncSupplierSheetsStatus();
              setSyncResultSupplier(r.data);
              if (r.data.status === 'done' || r.data.status === 'error') {
                clearInterval(interval);
                setSyncing(null);
                loadStats();
              }
            } catch {}
          }, 2000);
          supplierPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Зупиняємо polling при розмонтуванні
  useEffect(() => () => clearInterval(supplierPollRef.current), []);

  // Bulk download: перевіряємо статус при монтуванні
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.bulkDownloadStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setBulkResult(data);
        if (data.status === 'running') {
          setBulkRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.bulkDownloadStatus();
              setBulkResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error') {
                clearInterval(interval);
                setBulkRunning(false);
                loadStats();
              }
            } catch {}
          }, 2000);
          bulkPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(bulkPollRef.current), []);

  // Telegram download: перевіряємо статус при монтуванні
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.tgDownloadStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setTgResult(data);
        if (data.status === 'running') {
          setTgRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.tgDownloadStatus();
              setTgResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setTgRunning(false);
                loadStats();
              }
            } catch {}
          }, 2000);
          tgPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(tgPollRef.current), []);

  // Price sync: відновлення polling при монтуванні
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.syncPriceSheetsStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setPriceResult(data);
        if (data.status === 'running') {
          setPriceRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.syncPriceSheetsStatus();
              setPriceResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setPriceRunning(false);
              }
            } catch {}
          }, 2000);
          pricePollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(pricePollRef.current), []);

  // Розсилка фото в TG — статус при відкритті вкладки
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.sendToTgStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setSendResult(data);
        if (data.status === 'running') {
          setSendRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.sendToTgStatus();
              setSendResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setSendRunning(false);
              }
            } catch {}
          }, 3000);
          sendPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(sendPollRef.current), []);

  async function handleSendToTg() {
    setSendRunning(true);
    setSendResult(null);
    try {
      await photoFixAPI.sendToTg(undefined, sendLimit);
    } catch (e) {
      setSendResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setSendRunning(false);
      return;
    }
    sendPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.sendToTgStatus();
        setSendResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(sendPollRef.current);
          setSendRunning(false);
        }
      } catch {}
    }, 3000);
  }

  async function handleOpenSendFailed() {
    const next = !sendFailedOpen;
    setSendFailedOpen(next);
    if (next) {
      try {
        const r = await photoFixAPI.sendToTgFailed();
        setSendFailedList(r.data.items || []);
      } catch {}
    }
  }

  // Clothing sync: відновлення polling при монтуванні
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.syncClothingCatalogStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setClothingResult(data);
        if (data.status === 'running') {
          setClothingRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.syncClothingCatalogStatus();
              setClothingResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setClothingRunning(false);
              }
            } catch {}
          }, 2000);
          clothingPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(clothingPollRef.current), []);

  async function handleSyncClothing() {
    setClothingRunning(true);
    setClothingResult(null);
    try {
      await photoFixAPI.syncClothingCatalog();
    } catch (e) {
      setClothingResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setClothingRunning(false);
      return;
    }
    clothingPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.syncClothingCatalogStatus();
        setClothingResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(clothingPollRef.current);
          setClothingRunning(false);
        }
      } catch {}
    }, 2000);
  }

  async function handleSyncPrice() {
    setPriceRunning(true);
    setPriceResult(null);
    try {
      await photoFixAPI.syncPriceSheets();
    } catch (e) {
      setPriceResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setPriceRunning(false);
      return;
    }
    pricePollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.syncPriceSheetsStatus();
        setPriceResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(pricePollRef.current);
          setPriceRunning(false);
        }
      } catch {}
    }, 2000);
  }

  // EASYBDPROM polling on mount
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.easybdpromStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setEasyResult(data);
        if (data.status === 'running') {
          setEasyRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.easybdpromStatus();
              setEasyResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setEasyRunning(false);
                loadStats();
              }
            } catch {}
          }, 2000);
          easyPollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Queue polling on mount
  useEffect(() => {
    let interval = null;
    (async () => {
      try {
        const s = await photoFixAPI.queueStatus();
        const data = s.data;
        if (!data || !data.status || data.status === 'idle') return;
        setQueueResult(data);
        if (data.status === 'running') {
          setQueueRunning(true);
          interval = setInterval(async () => {
            try {
              const r = await photoFixAPI.queueStatus();
              setQueueResult(r.data);
              if (r.data.status === 'done' || r.data.status === 'error' || r.data.status === 'stopped') {
                clearInterval(interval);
                setQueueRunning(false);
              }
            } catch {}
          }, 2000);
          queuePollRef.current = interval;
        }
      } catch {}
    })();
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  useEffect(() => () => clearInterval(queuePollRef.current), []);

  async function handleQueueStart(taskKeys) {
    setQueueRunning(true);
    setQueueResult(null);
    try {
      await photoFixAPI.queueStart(taskKeys);
    } catch (e) {
      setQueueResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setQueueRunning(false);
      return;
    }
    queuePollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.queueStatus();
        setQueueResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(queuePollRef.current);
          setQueueRunning(false);
        }
      } catch {}
    }, 2000);
  }

  useEffect(() => () => clearInterval(easyPollRef.current), []);

  async function handleEasyBdProm() {
    setEasyRunning(true);
    setEasyResult(null);
    try {
      await photoFixAPI.easybdprom(easyLimit, easyStartFrom.trim());
    } catch (e) {
      setEasyResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setEasyRunning(false);
      return;
    }
    easyPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.easybdpromStatus();
        setEasyResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(easyPollRef.current);
          setEasyRunning(false);
          loadStats();
        }
      } catch {}
    }, 2000);
  }

  async function handleTgDownload() {
    setTgRunning(true);
    setTgResult(null);
    try {
      await photoFixAPI.tgDownload();
    } catch (e) {
      setTgResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setTgRunning(false);
      return;
    }
    tgPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.tgDownloadStatus();
        setTgResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(tgPollRef.current);
          setTgRunning(false);
          loadStats();
        }
      } catch {}
    }, 2000);
  }

  async function handleCleanupStubs() {
    setStubCleanRunning(true);
    setStubCleanResult(null);
    try {
      const r = await photoFixAPI.cleanupStubs();
      setStubCleanResult(r.data);
    } catch (e) {
      setStubCleanResult({ error: e?.response?.data?.detail || 'Помилка' });
    } finally {
      setStubCleanRunning(false);
      loadStats();
    }
  }

  async function handleBulkDownload() {
    setBulkRunning(true);
    setBulkResult(null);
    try {
      await photoFixAPI.bulkDownload();
    } catch (e) {
      setBulkResult({ status: 'error', error: e?.response?.data?.detail || 'Помилка запуску' });
      setBulkRunning(false);
      return;
    }
    bulkPollRef.current = setInterval(async () => {
      try {
        const s = await photoFixAPI.bulkDownloadStatus();
        setBulkResult(s.data);
        if (s.data.status === 'done' || s.data.status === 'error' || s.data.status === 'stopped') {
          clearInterval(bulkPollRef.current);
          setBulkRunning(false);
          loadStats();
        }
      } catch {}
    }, 2000);
  }

  const loadIc = useCallback(async (q, st, pg) => {
    setIcLoading(true);
    try {
      const r = await photoFixAPI.getImageCache({ q: q ?? '', status: st ?? 'all', page: pg ?? 1, per_page: 50 });
      setIcItems(r.data.items);
      setIcTotal(r.data.total);
    } catch {}
    finally { setIcLoading(false); }
  }, []);

  function handleIcSearchChange(val) {
    setIcSearch(val);
    setIcPage(1);
    clearTimeout(icSearchTimer.current);
    icSearchTimer.current = setTimeout(() => loadIc(val, icStatus, 1), 400);
  }

  function handleIcStatusChange(val) {
    setIcStatus(val);
    setIcPage(1);
    loadIc(icSearch, val, 1);
  }

  function handleIcPage(delta) {
    const np = icPage + delta;
    setIcPage(np);
    loadIc(icSearch, icStatus, np);
  }

  function handleIcOpen() {
    setIcOpen(true);
    loadIc(icSearch, icStatus, icPage);
  }

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── Google Sheets синхронізація ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>

        {/* 1: КаталогТоваров (взуття) */}
        <SyncRow
          title="КаталогТоваров (стовпець A)"
          description="Замінює imgbox-посилання у стовпці A листа «КаталогТоваров» на прямі посилання з нашого сервера. Лог → лист «Замена»."
          running={syncing === 'catalog'}
          disabled={syncing !== null}
          result={syncResultCatalog}
          onRun={handleSyncCatalog}
          onStop={handleStopCatalog}
          resultLabel={(r) => {
            if (r.status === 'stopped') return 'Скасовано';
            return `Замінено: ${r.replaced}, вже було: ${r.skipped}`;
          }}
        />

        {/* 2: КаталогТоваровОдежда */}
        <SyncRow
          title="КаталогТоваровОдежда (стовпець C)"
          description="Замінює imgbox-посилання у стовпці C листа «КаталогТоваровОдежда» на прямі посилання з нашого сервера. Фоновий запуск."
          running={clothingRunning}
          disabled={clothingRunning || priceRunning || sendRunning || tgRunning || bulkRunning || syncing !== null}
          result={clothingResult}
          onRun={handleSyncClothing}
          onStop={() => photoFixAPI.syncClothingCatalogStop()}
          resultLabel={(r) => {
            if (r.status === 'running') return null;
            if (r.status === 'stopped') return `Зупинено: замінено ${r.replaced ?? 0} посилань`;
            if (r.status === 'error') return null;
            return `${r.message || `Замінено: ${r.replaced ?? 0} посилань`}`;
          }}
        />

        {/* 3: Таблиці постачальників */}
        <div>
          <SyncRow
            title="Таблиці постачальників (9 шт., =IMAGE формули)"
            description="Замінює =IMAGE(&quot;imgbox_url&quot;) у стовпці A всіх листів таблиць постачальників. Лог → «Замена таблицы». Фоновий запуск, прогрес оновлюється кожні 2 с."
            running={syncing === 'supplier'}
            disabled={syncing !== null}
            result={syncResultSupplier}
            onRun={handleSyncSupplier}
            onStop={() => photoFixAPI.syncSupplierSheetsStop()}
            resultLabel={(r) => {
              if (r.status === 'running') return null;
              if (r.status === 'stopped') return `Зупинено: замінено ${r.replaced ?? 0} формул у ${r.sheets_processed ?? 0} листах`;
              return `Замінено: ${r.replaced ?? 0} формул у ${r.sheets_processed ?? 0} листах${r.errors?.length ? ` · ⚠ ${r.errors.length} помилок` : ''}`;
            }}
          />
          <SupplierSheetsList />
        </div>

        {/* 4: Прайс-листи постачальників E→F */}
        <SyncRow
          title="Прайс-листи постачальників (199 шт., стовпець E → F)"
          description="Для кожного постачальника зі «Список поставщиков»: замінює imgbox-посилання у стовпці E основного листа (→ F) та листа ALLDROP ART зовнішньої таблиці (→ F). Фоновий запуск."
          running={priceRunning}
          disabled={priceRunning || clothingRunning || sendRunning || tgRunning || bulkRunning || syncing !== null}
          result={priceResult}
          onRun={handleSyncPrice}
          onStop={() => photoFixAPI.syncPriceSheetsStop()}
          resultLabel={(r) => {
            if (r.status === 'running') return null;
            if (r.status === 'stopped') return `Зупинено: замінено ${r.replaced ?? 0} посилань`;
            if (r.status === 'error') return null;
            return `${r.message || `Замінено: ${r.replaced ?? 0} посилань`}${r.errors?.length ? ` · ⚠ ${r.errors.length} помилок` : ''}`;
          }}
        />

        {/* 4.5: Розсилка всіх фото адміну в Telegram */}
        <SyncRow
          title="Надіслати фото в Telegram"
          description={`За один клік надсилає ${sendLimit} фото з кешу (image_cache) у чат адміну — пачками по 10, ~30 фото/хв, і завершується. Лишились ще — натисніть ще раз, продовжить з того самого місця.`}
          running={sendRunning}
          disabled={sendRunning || priceRunning || clothingRunning || tgRunning || bulkRunning || syncing !== null}
          result={sendResult}
          onRun={handleSendToTg}
          onStop={() => photoFixAPI.sendToTgStop()}
          extraControls={
            <input
              type="number"
              min={1}
              max={2000}
              value={sendLimit}
              onChange={e => setSendLimit(Math.max(1, Math.min(2000, Number(e.target.value) || 1)))}
              title="Сколько фото отправить за этот запуск"
              style={{
                width: 70, padding: '8px 8px', borderRadius: 7,
                border: '1px solid var(--border, #e5e7eb)',
                fontSize: 13, textAlign: 'center',
              }}
            />
          }
          resultLabel={(r) => {
            if (r.status === 'running') return null;
            if (r.status === 'stopped') return `Зупинено: надіслано ${r.sent ?? 0}/${r.total ?? 0}`;
            if (r.status === 'error') return r.message || 'Помилка';
            return r.progress || `Надіслано ${r.sent ?? 0}/${r.total ?? 0}`;
          }}
        />
        {sendResult && (sendResult.failed > 0) && (
          <div style={{ marginTop: -8, marginBottom: 8 }}>
            <button className="rt-btn rt-btn-sm" onClick={handleOpenSendFailed}>
              {sendFailedOpen ? '▲' : '▼'} Не надіслані фото ({sendResult.failed})
            </button>
            {sendFailedOpen && (
              <div style={{
                marginTop: 8, maxHeight: 260, overflowY: 'auto',
                background: 'var(--bg-card, #f9fafb)', border: '1px solid var(--border, #e5e7eb)',
                borderRadius: 8, padding: 8, fontSize: 12,
              }}>
                {sendFailedList.length === 0 && <div style={{ opacity: .6 }}>Немає / завантажується…</div>}
                {sendFailedList.map(it => (
                  <div key={it.id} style={{ padding: '4px 0', borderBottom: '1px solid var(--border, #eee)' }}>
                    <a href={it.url} target="_blank" rel="noreferrer">{it.url}</a>
                    <div style={{ color: '#c0392b' }}>{it.error}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 5: Завантаження фото з imgbox */}
        <SyncRow
          title="Завантажити фото з imgbox (коли сервіс відновиться)"
          description="Масово скачує всі відсутні фото напряму з imgbox і зберігає на нашому сервері. Запускати коли imgbox знову запрацює. Фоновий запуск."
          running={bulkRunning}
          disabled={bulkRunning || tgRunning || sendRunning || syncing !== null}
          result={bulkResult}
          onRun={handleBulkDownload}
          onStop={() => photoFixAPI.bulkDownloadStop()}
          resultLabel={(r) => {
            if (r.status === 'running') return null;
            if (r.status === 'stopped') return `Зупинено: збережено ${r.saved ?? 0} з ${r.total ?? 0}`;
            if (r.status === 'error') return null;
            return `Збережено: ${r.saved ?? 0}, пропущено: ${r.failed ?? 0}, всього: ${r.done ?? 0}`;
          }}
        />

        {/* 6: Завантаження через Telegram */}
        <SyncRow
          title="Завантажити фото через Telegram (кеш з попередніх пересилань)"
          description="Надсилає кожне відсутнє фото боту @alldropai_bot — Telegram скачує з imgbox або повертає з кешу, ми зберігаємо результат. ~1 фото/сек. Підпис повідомлення = URL товару."
          running={tgRunning}
          disabled={tgRunning || bulkRunning || sendRunning || syncing !== null}
          result={tgResult}
          onRun={handleTgDownload}
          onStop={() => photoFixAPI.tgDownloadStop()}
          resultLabel={(r) => {
            if (r.status === 'running') return null;
            if (r.status === 'stopped') return `Зупинено вручну: збережено ${r.saved ?? 0} з ${r.total ?? 0}`;
            if (r.status === 'error') return null;
            return `Збережено: ${r.saved ?? 0}, не вдалось: ${r.failed ?? 0}, всього: ${r.total ?? 0}`;
          }}
        />

        {/* 6b: Видалити заглушки з image_cache */}
        <div style={{
          padding: '12px 18px',
          borderRadius: 10,
          border: `1px solid var(--border, #e5e7eb)`,
          background: 'var(--bg-card, #f9fafb)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>
              Видалити збережені заглушки imgbox
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted, #888)' }}>
              Сканує image_cache, знаходить записи де збережено заглушку (менше 15 KB або відомий MD5) і видаляє їх — щоб Telegram-завантаження змогло повторити спробу.
            </div>
            {stubCleanResult && (
              <div style={{ fontSize: 12, marginTop: 6, color: stubCleanResult.error ? '#ef4444' : '#16a34a' }}>
                {stubCleanResult.error
                  ? `Помилка: ${stubCleanResult.error}`
                  : `Перевірено: ${stubCleanResult.checked}, видалено заглушок: ${stubCleanResult.deleted}, помилок: ${stubCleanResult.errors}`}
              </div>
            )}
          </div>
          <button
            onClick={handleCleanupStubs}
            disabled={stubCleanRunning || tgRunning}
            style={{
              padding: '7px 16px', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              background: stubCleanRunning ? '#9ca3af' : '#ef4444',
              color: '#fff', border: 'none', whiteSpace: 'nowrap',
            }}
          >
            {stubCleanRunning ? 'Очищення…' : 'Очистити заглушки'}
          </button>
        </div>

        {/* 7: EASYBDPROM — відновлення через зовнішні таблиці + Telegram */}
        <div style={{
          padding: '14px 18px',
          borderRadius: 10,
          border: `1px solid ${easyRunning || easyResult?.status === 'running' ? '#3b82f6' : 'var(--border, #e5e7eb)'}`,
          background: 'var(--bg-card, #f9fafb)',
          transition: 'border-color 0.2s',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>
                EASYBDPROM — відновити фото через зовнішні таблиці
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted, #888)' }}>
                Для кожного failed-фото знаходить артикул у листі EASYBDPROM зовнішньої таблиці постачальника,
                відправляє фото в Telegram (606376367) і зберігає на alldrop.ink. Обробляє по постачальнику.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
              {easyResult && !easyRunning && easyResult.status !== 'error' && (
                <div style={{ fontSize: 13, color: 'var(--ok, #22a84a)' }}>
                  ✓ Збережено: {easyResult.saved ?? 0}{easyResult.errors > 0 ? ` · ⚠ ${easyResult.errors} помилок` : ''}
                </div>
              )}
              {easyResult?.status === 'error' && (
                <div style={{ fontSize: 13, color: '#c0392b', maxWidth: 260 }}>
                  ⚠ {easyResult.error || easyResult.message}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--muted,#888)', whiteSpace: 'nowrap' }}>Старт з:</label>
                <input
                  type="text"
                  value={easyStartFrom}
                  onChange={e => setEasyStartFrom(e.target.value)}
                  disabled={easyRunning}
                  placeholder="назва постачальника"
                  style={{
                    width: 160, padding: '6px 8px', borderRadius: 6,
                    border: '1px solid var(--border,#e5e7eb)',
                    background: 'var(--bg-main,#fff)', color: 'inherit',
                    fontSize: 13,
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: 'var(--muted,#888)', whiteSpace: 'nowrap' }}>Ліміт:</label>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={easyLimit}
                  onChange={e => setEasyLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
                  disabled={easyRunning}
                  style={{
                    width: 60, padding: '6px 8px', borderRadius: 6,
                    border: '1px solid var(--border,#e5e7eb)',
                    background: 'var(--bg-main,#fff)', color: 'inherit',
                    fontSize: 13, textAlign: 'center',
                  }}
                />
              </div>
              {(easyRunning || easyResult?.status === 'running') && (
                <button
                  onClick={() => photoFixAPI.easybdpromStop()}
                  style={{
                    padding: '8px 16px', borderRadius: 7,
                    border: '1px solid #ef4444', background: 'transparent',
                    color: '#ef4444', fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                  ⏹ Стоп
                </button>
              )}
              <button
                onClick={handleEasyBdProm}
                disabled={easyRunning || tgRunning || bulkRunning || sendRunning || syncing !== null}
                style={{
                  padding: '8px 20px', borderRadius: 7, border: 'none',
                  background: (easyRunning || tgRunning || bulkRunning || syncing !== null) ? '#9ca3af' : '#2563eb',
                  color: '#fff', fontWeight: 600, fontSize: 13,
                  cursor: (easyRunning || tgRunning || bulkRunning || syncing !== null) ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}>
                {easyRunning ? '⏳ Виконується…' : '▶ Запустити'}
              </button>
            </div>
          </div>
          {(easyRunning || easyResult?.status === 'running') && easyResult?.progress && (
            <div style={{
              marginTop: 10, fontSize: 12, color: 'var(--lg-text-2,#6b7280)',
              padding: '6px 10px', borderRadius: 6,
              background: 'rgba(59,130,246,0.07)',
            }}>
              {easyResult.progress}
              {easyResult.saved != null && (
                <span style={{ marginLeft: 12, fontWeight: 600, color: 'var(--lg-text,inherit)' }}>
                  ✅ {easyResult.saved}/{easyResult.limit ?? easyLimit}
                  {easyResult.errors > 0 && <span style={{ color: '#ef4444' }}> · ❌ {easyResult.errors}</span>}
                </span>
              )}
            </div>
          )}
          {/* Постачальники не знайдені в Список поставщиков */}
          {!easyRunning && easyResult?.unmatched_suppliers?.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                ⚠ Не знайдено в «Список поставщиков» ({easyResult.unmatched_suppliers.length}) — додайте або виправте назву:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {easyResult.unmatched_suppliers.map((s, i) => (
                  <span key={i} style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11,
                    background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.4)',
                    color: 'var(--lg-text,inherit)',
                  }}>
                    {s.name} ({s.items}) <span style={{ opacity: 0.5 }}>«{s.norm}»</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Знайдені, але без листа EASYBDPROM */}
          {!easyRunning && easyResult?.no_sheet_suppliers?.length > 0 && (
            <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#b91c1c', marginBottom: 6 }}>
                ✗ Знайдено в списку, але нема листа «EASYBDPROM» ({easyResult.no_sheet_suppliers.length}) — додайте лист в зовнішню таблицю:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {easyResult.no_sheet_suppliers.map((s, i) => (
                  <span key={i} style={{
                    padding: '2px 8px', borderRadius: 4, fontSize: 11,
                    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                    color: 'var(--lg-text,inherit)',
                  }}>
                    {s.name} ({s.items})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ── Планер задач ── */}
      <QueueSection
        running={queueRunning}
        result={queueResult}
        anyBusy={queueRunning || tgRunning || bulkRunning || priceRunning || syncing !== null}
        onStart={handleQueueStart}
        onStop={() => photoFixAPI.queueStop()}
      />

      {/* Підсумок */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {CATEGORIES.map(c => (
            <div key={c.key}
              onClick={() => setCategory(c.key)}
              style={{
                padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                background: category === c.key ? 'var(--accent, #2563eb)' : 'var(--bg-card, #f4f4f4)',
                color: category === c.key ? '#fff' : 'inherit',
                fontSize: 13, fontWeight: 500,
              }}>
              {c.label}
              <span style={{
                marginLeft: 8,
                background: category === c.key ? 'rgba(255,255,255,0.25)' : 'var(--badge-bg, #e5e7eb)',
                color: category === c.key ? '#fff' : 'var(--lg-text)',
                borderRadius: 10, padding: '1px 7px', fontSize: 12,
              }}>
                {stats[c.key] ?? '…'}
              </span>
            </div>
          ))}
          {stats.cache_imgbox != null && (
            <div style={{ fontSize: 12, color: 'var(--lg-text-3)', marginLeft: 4 }}>
              🗄 кеш imgbox: <strong style={{ color: 'var(--lg-green, #22a84a)' }}>{stats.cache_imgbox.toLocaleString()}</strong> фото
            </div>
          )}
          {stats.tg_failed > 0 && (
            <div style={{ fontSize: 12, color: 'var(--lg-red)', marginLeft: 4, cursor: 'pointer' }} onClick={toggleFailedOnly}>
              ⚠ TG помилки: <strong>{stats.tg_failed.toLocaleString()}</strong>
            </div>
          )}
        </div>
      )}

      {/* Пошук + підказка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 480 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--lg-text-3)', pointerEvents: 'none', fontSize: 14 }}>🔍</span>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Пошук за URL, SKU, назвою…"
            style={{
              width: '100%',
              padding: '7px 10px 7px 32px',
              borderRadius: 7,
              border: '1px solid var(--border, #d1d5db)',
              background: 'var(--bg-card)',
              color: 'var(--lg-text)',
              fontSize: 13,
              outline: 'none',
            }}
          />
          {search && (
            <button
              onClick={() => handleSearch('')}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--lg-text-3)', fontSize: 16, lineHeight: 1, padding: 0 }}>
              ×
            </button>
          )}
        </div>
        <button
          onClick={toggleFailedOnly}
          style={{
            ...btnStyle(),
            background: tgFailedOnly ? 'rgba(234,82,82,0.15)' : 'transparent',
            border: `1px solid ${tgFailedOnly ? 'var(--lg-red)' : 'var(--border, #d1d5db)'}`,
            color: tgFailedOnly ? 'var(--lg-red)' : 'var(--lg-text-2)',
            fontSize: 12,
            padding: '6px 10px',
            whiteSpace: 'nowrap',
          }}>
          {tgFailedOnly ? '✗ TG помилки (скинути)' : 'TG помилки'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--muted, #888)' }}>
          {search || tgFailedOnly ? `Знайдено: ${total}` : `Всього: ${total}`} · натисніть рядок для завантаження
        </div>
      </div>

      {/* Таблиця */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 }}>
          <thead>
            <tr>
              <th style={thStyle()}>#</th>
              {catDef.columns.map(col => (
                <th key={col.key}
                  onClick={() => handleSort(col.key)}
                  style={thStyle(col.width, col.flex)}>
                  {col.label}
                  {sortBy === col.key && (
                    <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={catDef.columns.length + 1} style={{ textAlign: 'center', padding: 24, color: 'var(--lg-text-3)' }}>Завантаження…</td></tr>
            ) : loadError ? (
              <tr><td colSpan={catDef.columns.length + 1} style={{ textAlign: 'center', padding: 24 }}>
                Ошибка загрузки.{' '}
                <button className="btn btn-sm btn-secondary" onClick={() => loadItems(category, offset, sortBy, sortDir, search, tgFailedOnly)}>Повторить</button>
              </td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={catDef.columns.length + 1} style={{ textAlign: 'center', padding: 24, color: 'var(--ok, #22a84a)' }}>✓ Всі фото збережені!</td></tr>
            ) : items.map((item, i) => (
              <tr key={item.id}
                onClick={() => setSelected(item)}
                style={{ cursor: 'pointer' }}
                className="pf-row">
                <td style={tdStyle()}>
                  {offset + i + 1}
                  {item.tg_failed && (
                    <span style={{ marginLeft: 5, fontSize: 10, background: 'rgba(234,82,82,0.15)', color: 'var(--lg-red)', borderRadius: 4, padding: '1px 4px', verticalAlign: 'middle', fontWeight: 600 }}>TG✗</span>
                  )}
                </td>
                {catDef.columns.map(col => (
                  <td key={col.key}
                    style={tdStyle(col.width, col.flex)}
                    onClick={col.key === 'photo_url' ? async (e) => {
                      e.stopPropagation();
                      if (category === 'supplier_items_broken') {
                        window.open(item.photo_url, '_blank');
                      } else {
                        try { await navigator.clipboard.writeText(item.photo_url); } catch {}
                        setSelected(item);
                      }
                    } : undefined}>
                    {col.key === 'photo_url'
                      ? (category === 'supplier_items_broken'
                          ? <a href={item.photo_url} target="_blank" rel="noreferrer"
                              style={{ color: 'var(--accent, #2563eb)', wordBreak: 'break-all' }}
                              onClick={e => e.stopPropagation()}>
                              {item.photo_url}
                            </a>
                          : <span style={{ color: 'var(--accent, #2563eb)', wordBreak: 'break-all' }}>{item.photo_url}</span>
                        )
                      : (item[col.key] || '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагінація */}
      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, fontSize: 13 }}>
          <button onClick={() => handlePage(-1)} disabled={offset === 0}
            style={btnStyle()}>← Назад</button>
          <span style={{ color: 'var(--lg-text-2)' }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} з {total}
          </span>
          <button onClick={() => handlePage(1)} disabled={offset + PAGE_SIZE >= total}
            style={btnStyle()}>Вперед →</button>
        </div>
      )}

      {/* Модалка завантаження */}
      {selected && (
        <UploadModal
          item={selected}
          category={category}
          onClose={() => setSelected(null)}
          onSuccess={() => {
            setSelected(null);
            loadStats();
            loadItems(category, offset, sortBy, sortDir, search, tgFailedOnly);
          }}
        />
      )}

      {/* ── Image Cache viewer ── */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>База кешу фото</span>
          <button onClick={handleIcOpen} style={{ ...btnStyle(), fontSize: 12 }}>
            {icOpen ? 'Оновити' : 'Відкрити'}
          </button>
          {icOpen && (
            <button onClick={() => setIcOpen(false)} style={{ ...btnStyle(), fontSize: 12 }}>
              Сховати
            </button>
          )}
        </div>
        {icOpen && (
          <ImageCacheSection
            items={icItems}
            total={icTotal}
            page={icPage}
            loading={icLoading}
            search={icSearch}
            status={icStatus}
            onSearchChange={handleIcSearchChange}
            onStatusChange={handleIcStatusChange}
            onPage={handleIcPage}
          />
        )}
      </div>

      <style>{`
        .pf-row:hover td { background: var(--hover-bg, rgba(37,99,235,0.06)); }
        .pf-row td { transition: background 0.1s; }
        @keyframes pf-progress {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

    </div>
  );
}

// ── Рядок синхронізації Google Sheets ────────────────────────────────────────
function SyncRow({ title, description, running, disabled, result, onRun, onStop, resultLabel, extraControls }) {
  const isRunning = result?.status === 'running' || running;
  const isError   = result?.status === 'error' || !!result?.error;
  const isDone    = !!result && !isRunning && !isError;
  const label     = result && !isRunning ? resultLabel(result) : null;

  return (
    <div style={{
      padding: '14px 18px',
      borderRadius: 10,
      border: `1px solid ${isRunning ? '#3b82f6' : 'var(--border, #e5e7eb)'}`,
      background: 'var(--bg-card, #f9fafb)',
      transition: 'border-color 0.2s',
    }}>
      {/* Верхній рядок: заголовок + кнопка */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{title}</div>
          <div style={{ fontSize: 12, color: 'var(--muted, #888)' }}
            dangerouslySetInnerHTML={{ __html: description }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {isDone && label && (
            <div style={{ fontSize: 13, color: 'var(--ok, #22a84a)' }}>✓ {label}</div>
          )}
          {isError && (
            <div style={{ fontSize: 13, color: '#c0392b', maxWidth: 260 }}>
              ⚠ {result.error || result.message}
            </div>
          )}
          {isRunning && onStop && (
            <button
              onClick={onStop}
              style={{
                padding: '8px 16px',
                borderRadius: 7,
                border: '1px solid #ef4444',
                background: 'transparent',
                color: '#ef4444',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}>
              ⏹ Стоп
            </button>
          )}
          {extraControls && !isRunning && extraControls}
          <button
            onClick={onRun}
            disabled={disabled}
            style={{
              padding: '8px 20px',
              borderRadius: 7,
              border: 'none',
              background: disabled ? '#9ca3af' : '#2563eb',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: disabled ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
              minWidth: 110,
            }}>
            {isRunning ? '⏳ Виконується…' : '↻ Оновити'}
          </button>
        </div>
      </div>

      {/* Рядок прогресу — тільки під час виконання */}
      {isRunning && result && (
        <div style={{
          marginTop: 10,
          padding: '8px 12px',
          borderRadius: 6,
          background: 'rgba(37,99,235,0.10)',
          fontSize: 12,
          color: 'var(--lg-text)',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>📋 {result.progress || 'Запуск…'}</span>
            {result.replaced != null && (
              <span style={{ fontWeight: 600 }}>Замінено: {result.replaced}</span>
            )}
            {result.saved != null && result.replaced == null && (
              <span style={{ fontWeight: 600 }}>Збережено: {result.saved}</span>
            )}
          </div>
          {result.last_error && (
            <div style={{ color: '#f59e0b', fontSize: 11, wordBreak: 'break-all' }}>
              ⚠ {result.last_error}
            </div>
          )}
          {result.table && (
            <div style={{ color: 'var(--lg-text-2)' }}>
              <strong style={{ color: 'var(--lg-text)' }}>{result.table}</strong>
              {result.sheet && <span> → {result.sheet}</span>}
            </div>
          )}
          {result.supplier && !result.table && (
            <div style={{ color: 'var(--lg-text)' }}>
              <strong>{result.supplier}</strong>
            </div>
          )}
          {/* Анімований індикатор */}
          <div style={{
            height: 3, borderRadius: 2,
            background: 'linear-gradient(90deg, #2563eb 0%, #93c5fd 50%, #2563eb 100%)',
            backgroundSize: '200% 100%',
            animation: 'pf-progress 1.5s linear infinite',
          }} />
        </div>
      )}
    </div>
  );
}

// ── Модалка upload ────────────────────────────────────────────────────────────
function UploadModal({ item, category, onClose, onSuccess }) {
  const [dragging,  setDragging]  = useState(false);
  const [preview,   setPreview]   = useState(null);
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState('');
  const [done,      setDone]      = useState(false);
  const [copied,    setCopied]    = useState(false);
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f || !f.type.startsWith('image/')) { setError('Потрібен файл-зображення'); return; }
    setFile(f);
    setError('');
    setDone(false);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(item.photo_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // Paste from clipboard (Ctrl+V)
  useEffect(() => {
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imgItem = items.find(it => it.type.startsWith('image/'));
      if (imgItem) {
        const f = imgItem.getAsFile();
        if (f) handleFile(f);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  const handleUpload = async () => {
    if (!file) { setError('Спочатку виберіть файл'); return; }
    setUploading(true); setError('');
    try {
      await photoFixAPI.upload(item.photo_url, file);
      setDone(true);
      setTimeout(() => onSuccess(), 800);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка завантаження');
    } finally { setUploading(false); }
  };

  const catLabel = CATEGORIES.find(c => c.key === category)?.label || '';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        {/* Заголовок */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Завантаження фото</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--lg-text-2)' }}>×</button>
        </div>

        {/* Інфо про товар */}
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-muted, #f9fafb)', borderRadius: 6, fontSize: 12 }}>
          <div style={{ color: 'var(--lg-text-3)', marginBottom: 2 }}>{catLabel}</div>
          <div style={{ fontWeight: 500 }}>
            <span
              onClick={() => navigator.clipboard.writeText(item.sku)}
              title="Скопіювати артикул"
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >{item.sku}</span>
            {item.name ? ` · ${item.name}` : ''}{item.supplier_name ? ` · ${item.supplier_name}` : ''}
          </div>
          <div
            onClick={handleCopyUrl}
            title="Натисніть, щоб скопіювати"
            style={{
              color: copied ? 'var(--lg-green, #22a84a)' : '#2563eb',
              wordBreak: 'break-all', marginTop: 4,
              cursor: 'pointer', userSelect: 'none',
              transition: 'color 0.2s',
            }}>
            {copied ? '✓ Скопійовано!' : item.photo_url}
          </div>
        </div>

        {/* Зона перетягування */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
            borderRadius: 10,
            padding: 24,
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'rgba(37,99,235,0.05)' : 'var(--bg-muted, #f9fafb)',
            transition: 'all 0.15s',
            marginBottom: 12,
            minHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 8,
          }}>
          {preview ? (
            <img src={preview} alt="" style={{ maxHeight: 160, maxWidth: '100%', borderRadius: 6, objectFit: 'contain' }} />
          ) : (
            <>
              <div style={{ fontSize: 32 }}>📁</div>
              <div style={{ fontSize: 13, color: 'var(--lg-text-3)', lineHeight: 1.6 }}>
                Перетягніть фото сюди або натисніть для вибору<br />
                <span style={{ fontSize: 12 }}>або вставте з буфера обміну <kbd style={{ fontSize: 11, padding: '1px 5px', borderRadius: 3, border: '1px solid var(--border, #d1d5db)', background: 'var(--bg-main)', fontFamily: 'monospace' }}>Ctrl+V</kbd></span>
              </div>
            </>
          )}
          <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])} />
        </div>

        {file && (
          <div style={{ fontSize: 12, color: 'var(--lg-text-2)', marginBottom: 8 }}>
            📎 {file.name} ({(file.size / 1024).toFixed(0)} KB)
          </div>
        )}

        {error && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 8 }}>⚠ {error}</div>}
        {done  && <div style={{ color: '#22a84a', fontSize: 13, marginBottom: 8 }}>✓ Збережено!</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnStyle()}>Скасувати</button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading || done}
            style={{ ...btnStyle(), background: '#2563eb', color: '#fff', opacity: (!file || uploading || done) ? 0.6 : 1 }}>
            {uploading ? 'Завантаження…' : done ? '✓ Готово' : 'Зберегти фото'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Порядок таблиць постачальників ───────────────────────────────────────────
function SupplierSheetsList() {
  const [open,     setOpen]     = useState(false);
  const [sheets,   setSheets]   = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [dragIdx,  setDragIdx]  = useState(null);
  const [dragOver, setDragOver] = useState(null);

  async function load() {
    try {
      const r = await photoFixAPI.getSupplierSheets();
      setSheets(r.data);
    } catch {}
  }

  function toggle() {
    if (!open && !sheets) load();
    setOpen(v => !v);
  }

  function onDragStart(e, idx) {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragOver(idx); return; }
    setDragOver(idx);
    const next = [...sheets];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setSheets(next);
    setDragIdx(idx);
  }

  async function onDragEnd() {
    setDragIdx(null);
    setDragOver(null);
    if (!sheets) return;
    setSaving(true);
    try { await photoFixAPI.saveSupplierSheetsOrder(sheets); } catch {}
    setSaving(false);
  }

  return (
    <div style={{ marginTop: 4 }}>
      <button
        onClick={toggle}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 12, color: 'var(--lg-text-3)', padding: '4px 0',
          display: 'flex', alignItems: 'center', gap: 5,
        }}>
        <span style={{ fontSize: 10 }}>{open ? '▼' : '▶'}</span>
        Порядок таблиць {saving ? '· збереження…' : ''}
      </button>

      {open && (
        <div style={{
          marginTop: 6, padding: '10px 12px',
          borderRadius: 8, border: '1px solid var(--border, #e5e7eb)',
          background: 'var(--bg-card)',
          display: 'flex', flexDirection: 'column', gap: 3,
        }}>
          {!sheets ? (
            <div style={{ fontSize: 12, color: 'var(--lg-text-3)' }}>Завантаження…</div>
          ) : sheets.map((s, idx) => (
            <div
              key={s.id}
              draggable
              onDragStart={e => onDragStart(e, idx)}
              onDragOver={e => onDragOver(e, idx)}
              onDragEnd={onDragEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 8px', borderRadius: 6,
                background: dragOver === idx ? 'rgba(37,99,235,0.08)' : 'transparent',
                border: '1px solid var(--border, #e5e7eb)',
                cursor: 'grab', userSelect: 'none', fontSize: 12,
              }}>
              <span style={{ color: 'var(--lg-text-3)', fontSize: 13 }}>⠿</span>
              <span style={{ color: 'var(--lg-text-3)', minWidth: 18 }}>{idx + 1}.</span>
              <span style={{ flex: 1, color: 'var(--lg-text)' }}>
                {s.title || <span style={{ color: 'var(--lg-text-3)', fontStyle: 'italic' }}>невідома таблиця</span>}
              </span>
              <span style={{ color: 'var(--lg-text-3)', fontSize: 11, fontFamily: 'monospace' }}>
                {s.id.slice(0, 12)}…
              </span>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--lg-text-3)', marginTop: 4 }}>
            Перетягніть рядки для зміни порядку · назви з'являться після першого запуску синхронізації
          </div>
        </div>
      )}
    </div>
  );
}

// ── Планер задач ─────────────────────────────────────────────────────────────
const QUEUE_TASK_DEFS = [
  { key: 'catalog',  label: 'КаталогТоваров (стовпець A)' },
  { key: 'clothing', label: 'КаталогТоваровОдежда (стовпець C)' },
  { key: 'supplier', label: 'Таблиці постачальників' },
  { key: 'price',    label: 'Прайс-листи E→F (199 постач.)' },
  { key: 'bulk',     label: 'Завантаження з imgbox' },
  { key: 'telegram', label: 'Завантаження через Telegram' },
];

function QueueSection({ running, result, anyBusy, onStart, onStop }) {
  const [tasks, setTasks] = useState(
    QUEUE_TASK_DEFS.map((t, i) => ({ ...t, checked: i < 3, id: t.key }))
  );
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  function toggleCheck(key) {
    setTasks(ts => ts.map(t => t.key === key ? { ...t, checked: !t.checked } : t));
  }

  function onDragStart(e, idx) {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDragOver(e, idx) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragOver(idx); return; }
    setDragOver(idx);
    const next = [...tasks];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setTasks(next);
    setDragIdx(idx);
  }

  function onDragEnd() { setDragIdx(null); setDragOver(null); }

  function handleStart() {
    const keys = tasks.filter(t => t.checked).map(t => t.key);
    if (keys.length) onStart(keys);
  }

  const isRunning = running || result?.status === 'running';
  const isDone    = result?.status === 'done' || result?.status === 'stopped';
  const doneTasks = result?.done_tasks || [];
  const total     = result?.total || 0;
  const currentKey = result?.current;
  const detail    = result?.task_detail;

  // Відсоток: скільки задач повністю завершено
  const pct = total > 0 ? Math.round((doneTasks.length / total) * 100) : 0;

  return (
    <div style={{
      marginTop: 8,
      padding: '16px 18px',
      borderRadius: 10,
      border: `2px solid ${isRunning ? '#7c3aed' : 'var(--border, #e5e7eb)'}`,
      background: 'var(--bg-card, #f9fafb)',
      transition: 'border-color 0.2s',
    }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>📋 Планер задач</div>
      <div style={{ fontSize: 12, color: 'var(--muted, #888)', marginBottom: 14 }}>
        Позначте задачі та перетягніть у потрібному порядку — вони виконаються по черзі автоматично.
      </div>

      {/* Список задач */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
        {tasks.map((t, idx) => {
          const doneEntry = doneTasks.find(d => d.key === t.key);
          const isCurrent = isRunning && t.key === currentKey;
          const isPending = isRunning && !doneEntry && !isCurrent;
          return (
            <div
              key={t.key}
              draggable={!isRunning}
              onDragStart={e => onDragStart(e, idx)}
              onDragOver={e => onDragOver(e, idx)}
              onDragEnd={onDragEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '7px 10px', borderRadius: 7,
                background: isCurrent
                  ? 'rgba(124,58,237,0.12)'
                  : dragOver === idx ? 'rgba(37,99,235,0.10)' : 'var(--bg-main, transparent)',
                border: `1px solid ${isCurrent ? '#7c3aed' : 'var(--border,#e5e7eb)'}`,
                cursor: isRunning ? 'default' : 'grab',
                userSelect: 'none',
                transition: 'background 0.15s',
              }}>
              {/* Drag handle */}
              <span style={{ color: '#9ca3af', fontSize: 14, cursor: isRunning ? 'default' : 'grab' }}>⠿</span>

              {/* Checkbox */}
              <input
                type="checkbox"
                checked={t.checked}
                disabled={isRunning}
                onChange={() => toggleCheck(t.key)}
                style={{ width: 15, height: 15, cursor: isRunning ? 'default' : 'pointer', flexShrink: 0 }}
              />

              {/* Назва */}
              <span style={{
                flex: 1, fontSize: 13,
                color: !t.checked ? 'var(--muted,#9ca3af)' : 'inherit',
                textDecoration: !t.checked ? 'line-through' : 'none',
              }}>
                {idx + 1}. {t.label}
              </span>

              {/* Статус */}
              {doneEntry && (
                <span style={{ fontSize: 12, color: doneEntry.ok ? '#22a84a' : '#ef4444', whiteSpace: 'nowrap' }}>
                  {doneEntry.ok ? '✓' : '⚠'} {doneEntry.info}
                </span>
              )}
              {isCurrent && <span style={{ fontSize: 12, color: '#7c3aed', whiteSpace: 'nowrap' }}>⏳ виконується…</span>}
              {isPending && t.checked && <span style={{ fontSize: 12, color: '#9ca3af' }}>○ очікує</span>}
            </div>
          );
        })}
      </div>

      {/* Кнопки */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={handleStart}
          disabled={anyBusy || isRunning}
          style={{
            padding: '9px 24px', borderRadius: 7, border: 'none',
            background: anyBusy || isRunning ? '#9ca3af' : '#7c3aed',
            color: '#fff', fontWeight: 700, fontSize: 13,
            cursor: anyBusy || isRunning ? 'not-allowed' : 'pointer',
          }}>
          {isRunning ? '⏳ Виконується…' : '▶ Запустити чергу'}
        </button>

        {isRunning && (
          <button
            onClick={onStop}
            style={{
              padding: '9px 18px', borderRadius: 7,
              border: '1px solid #ef4444', background: 'transparent',
              color: '#ef4444', fontWeight: 600, fontSize: 13, cursor: 'pointer',
            }}>
            ⏹ Стоп
          </button>
        )}

        {isDone && (
          <span style={{ fontSize: 13, color: result.status === 'stopped' ? '#d97706' : '#22a84a', fontWeight: 500 }}>
            {result.status === 'stopped' ? '⏹ Зупинено' : '✓ Всі задачі виконано'} ({doneTasks.length}/{total})
          </span>
        )}
      </div>

      {/* Прогрес-бар + деталі поточної задачі */}
      {isRunning && (
        <div style={{ marginTop: 12 }}>
          {/* Загальний прогрес */}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--lg-text-2)', marginBottom: 4 }}>
            <span>{result?.progress || 'Запуск…'}</span>
            <span>{pct}%</span>
          </div>
          <div style={{ height: 4, borderRadius: 2, background: 'var(--border,#e5e7eb)', marginBottom: 8 }}>
            <div style={{
              height: '100%', borderRadius: 2,
              background: 'linear-gradient(90deg,#7c3aed,#a78bfa)',
              width: `${Math.max(pct, 3)}%`,
              transition: 'width 0.4s',
            }} />
          </div>

          {/* Деталі поточної задачі (progress + supplier/table/sheet) */}
          {detail && (
            <div style={{
              padding: '8px 12px', borderRadius: 6,
              background: 'rgba(124,58,237,0.10)',
              fontSize: 12, color: 'var(--lg-text)',
              display: 'flex', flexDirection: 'column', gap: 3,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>📋 {detail.progress || '…'}</span>
                {detail.replaced != null && <span style={{ fontWeight: 600 }}>Замінено: {detail.replaced}</span>}
                {detail.saved != null && <span style={{ fontWeight: 600 }}>Збережено: {detail.saved}</span>}
              </div>
              {(detail.table || detail.supplier) && (
                <div style={{ color: 'var(--lg-text-2)' }}>
                  <strong style={{ color: 'var(--lg-text)' }}>{detail.table || detail.supplier}</strong>
                  {detail.sheet && <span> → {detail.sheet}</span>}
                </div>
              )}
              {/* Анімований індикатор */}
              <div style={{
                height: 3, borderRadius: 2,
                background: 'linear-gradient(90deg,#7c3aed 0%,#c4b5fd 50%,#7c3aed 100%)',
                backgroundSize: '200% 100%',
                animation: 'pf-progress 1.5s linear infinite',
              }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Image Cache viewer ────────────────────────────────────────────────────────
function ImageCacheSection({ items, total, page, loading, search, status, onSearchChange, onStatusChange, onPage }) {
  const perPage = 50;
  return (
    <div style={{ border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, overflow: 'hidden' }}>
      {/* Фільтри */}
      <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: 'var(--bg-card, #f9fafb)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Пошук по URL або імені файлу…"
          style={{ flex: 1, minWidth: 220, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border, #d1d5db)', fontSize: 13, background: 'var(--bg-main)', color: 'var(--lg-text)' }}
        />
        <select
          value={status}
          onChange={e => onStatusChange(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border, #d1d5db)', fontSize: 13, background: 'var(--bg-main)', color: 'var(--lg-text)' }}
        >
          <option value="all">Всі статуси</option>
          <option value="ok">OK</option>
          <option value="failed">Failed</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--muted, #888)', whiteSpace: 'nowrap' }}>
          {loading ? 'Завантаження…' : `Всього: ${total}`}
        </span>
      </div>

      {/* Таблиця */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={thStyle(60)}>ID</th>
              <th style={thStyle(null, 1)}>original_url</th>
              <th style={thStyle(null, 1)}>local_url</th>
              <th style={thStyle(90)}>Статус</th>
              <th style={thStyle(130)}>Дата</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: 'center', color: 'var(--muted)' }}>Нічого не знайдено</td></tr>
            )}
            {items.map(row => (
              <tr key={row.id} className="pf-row">
                <td style={tdStyle(60)}>{row.id}</td>
                <td style={tdStyle(null, 1)}>
                  <a href={row.original_url} target="_blank" rel="noreferrer"
                    style={{ color: 'var(--accent, #2563eb)', wordBreak: 'break-all' }}>
                    {row.original_url}
                  </a>
                </td>
                <td style={tdStyle(null, 1)}>
                  <a href={row.local_url} target="_blank" rel="noreferrer"
                    style={{ color: 'var(--accent, #2563eb)', wordBreak: 'break-all' }}>
                    {row.local_url}
                  </a>
                </td>
                <td style={{ ...tdStyle(90), color: row.tg_status === 'failed' ? '#ef4444' : '#16a34a', fontWeight: 600 }}>
                  {row.tg_status}
                </td>
                <td style={tdStyle(130)}>{row.created_at ? row.created_at.slice(0, 16).replace('T', ' ') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Пагінація */}
      {total > perPage && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderTop: '1px solid var(--border, #e5e7eb)', fontSize: 13 }}>
          <button onClick={() => onPage(-1)} disabled={page <= 1} style={btnStyle()}>← Назад</button>
          <span style={{ color: 'var(--muted)' }}>
            {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} з {total}
          </span>
          <button onClick={() => onPage(1)} disabled={page * perPage >= total} style={btnStyle()}>Вперед →</button>
        </div>
      )}
    </div>
  );
}

// ── Стилі ────────────────────────────────────────────────────────────────────
function thStyle(width, flex) {
  return {
    textAlign: 'left',
    padding: '8px 10px',
    background: 'var(--bg-header, #f3f4f6)',
    borderBottom: '2px solid var(--border, #e5e7eb)',
    borderRight: '1px solid var(--border, #e5e7eb)',
    fontWeight: 600,
    fontSize: 12,
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    width: width || undefined,
    ...(flex ? { minWidth: 120 } : {}),
  };
}

function tdStyle(width, flex) {
  return {
    padding: '7px 10px',
    borderBottom: '1px solid var(--border, #e5e7eb)',
    borderRight: '1px solid var(--border, #e5e7eb)',
    verticalAlign: 'middle',
    width: width || undefined,
    wordBreak: flex ? 'break-all' : undefined,
  };
}

function btnStyle() {
  return {
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid var(--border, #d1d5db)',
    background: 'var(--bg-card)',
    color: 'var(--lg-text)',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
  };
}

const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 9000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const modalStyle = {
  background: 'var(--bg-main, #fff)',
  borderRadius: 12,
  padding: 24,
  width: '90%',
  maxWidth: 560,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
